// 缓动函数 t: 0-1
export type EasingFunction = (t: number) => number;

export const Easing = {
  // 线性
  Linear: {
    None: function (t: number): number {
      return t;
    }
  },
  Ease: {
    In: function (t: number): number {
      return Math.pow(t, 1.7);
    }
  },
  // 二次
  Quadratic: {
    In: function (t: number): number {
      return t * t;
    },
    Out: function (t: number): number {
      return t * (2 - t);
    },
    InOut: function (t: number): number {
      if ((t *= 2) < 1) {
        return 0.5 * t * t;
      }
      return -0.5 * (--t * (t - 2) - 1);
    },
    style: "cubic-bezier(0.25, 0.46, 0.45, 0.94)"
  },
  // 三次
  Cubic: {
    In: function (t: number): number {
      return t * t * t;
    },
    Out: function (t: number): number {
      return --t * t * t + 1;
    },
    InOut: function (t: number): number {
      if ((t *= 2) < 1) {
        return 0.5 * t * t * t;
      }
      return 0.5 * ((t -= 2) * t * t + 2);
    }
  },
  // 四次
  Quartic: {
    In: function (t: number): number {
      return t * t * t * t;
    },
    Out: function (t: number): number {
      return 1 - --t * t * t * t;
    },
    InOut: function (t: number): number {
      if ((t *= 2) < 1) {
        return 0.5 * t * t * t * t;
      }
      return -0.5 * ((t -= 2) * t * t * t - 2);
    }
  },
  // 五次
  Quintic: {
    In: function (t: number): number {
      return t * t * t * t * t;
    },
    Out: function (t: number): number {
      return --t * t * t * t * t + 1;
    },
    InOut: function (t: number): number {
      if ((t *= 2) < 1) {
        return 0.5 * t * t * t * t * t;
      }
      return 0.5 * ((t -= 2) * t * t * t * t + 2);
    }
  },
  // 正弦曲线
  Sinusoidal: {
    In: function (t: number): number {
      return 1 - Math.sin(((1.0 - t) * Math.PI) / 2);
    },
    Out: function (t: number): number {
      return Math.sin((t * Math.PI) / 2);
    },
    InOut: function (t: number): number {
      return 0.5 * (1 - Math.sin(Math.PI * (0.5 - t)));
    }
  },
  // 指数
  Exponential: {
    In: function (t: number): number {
      return t === 0 ? 0 : Math.pow(1024, t - 1);
    },
    Out: function (t: number): number {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    },
    InOut: function (t: number): number {
      if (t === 0) {
        return 0;
      }
      if (t === 1) {
        return 1;
      }
      if ((t *= 2) < 1) {
        return 0.5 * Math.pow(1024, t - 1);
      }
      return 0.5 * (-Math.pow(2, -10 * (t - 1)) + 2);
    }
  },
  // 圆
  Circular: {
    In: function (t: number): number {
      return 1 - Math.sqrt(1 - t * t);
    },
    Out: function (t: number): number {
      return Math.sqrt(1 - --t * t);
    },
    InOut: function (t: number): number {
      if ((t *= 2) < 1) {
        return -0.5 * (Math.sqrt(1 - t * t) - 1);
      }
      return 0.5 * (Math.sqrt(1 - (t -= 2) * t) + 1);
    },
    style: "cubic-bezier(0.1, 0.57, 0.1, 1)" // Not properly "circular" but this looks better, it should be (0.075, 0.82, 0.165, 1)
  },
  // 松紧带
  Elastic: {
    In: function (t: number): number {
      if (t === 0) {
        return 0;
      }
      if (t === 1) {
        return 1;
      }
      return -Math.pow(2, 10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI);
    },
    Out: function (t: number): number {
      if (t === 0) {
        return 0;
      }
      if (t === 1) {
        return 1;
      }
      return Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
    },
    InOut: function (t: number): number {
      if (t === 0) {
        return 0;
      }
      if (t === 1) {
        return 1;
      }
      t *= 2;
      if (t < 1) {
        return (
          -0.5 * Math.pow(2, 10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI)
        );
      }
      return (
        0.5 * Math.pow(2, -10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI) + 1
      );
    }
  },
  // 后退
  Back: {
    In: function (t: number): number {
      const s = 1.70158;
      return t === 1 ? 1 : t * t * ((s + 1) * t - s);
    },
    Out: function (t: number): number {
      const s = 1.70158;
      // const s = 4;
      return t === 0 ? 0 : --t * t * ((s + 1) * t + s) + 1;
    },
    InOut: function (t: number): number {
      const s = 1.70158 * 1.525;
      if ((t *= 2) < 1) {
        return 0.5 * (t * t * ((s + 1) * t - s));
      }
      return 0.5 * ((t -= 2) * t * ((s + 1) * t + s) + 2);
    },
    style: "cubic-bezier(0.175, 0.885, 0.32, 1.275)"
  },
  // 弹跳
  Bounce: {
    In: function (t: number): number {
      return 1 - Easing.Bounce.Out(1 - t);
    },
    Out: function (t: number): number {
      if (t < 1 / 2.75) {
        return 7.5625 * t * t;
      } else if (t < 2 / 2.75) {
        return 7.5625 * (t -= 1.5 / 2.75) * t + 0.75;
      } else if (t < 2.5 / 2.75) {
        return 7.5625 * (t -= 2.25 / 2.75) * t + 0.9375;
      } else {
        return 7.5625 * (t -= 2.625 / 2.75) * t + 0.984375;
      }
    },
    InOut: function (t: number): number {
      if (t < 0.5) {
        return Easing.Bounce.In(t * 2) * 0.5;
      }
      return Easing.Bounce.Out(t * 2 - 1) * 0.5 + 0.5;
    }
  },
  // 生成指数
  generatePow: function (power = 4): {
    In(t: number): number;
    Out(t: number): number;
    InOut(t: number): number;
  } {
    power = power < Number.EPSILON ? Number.EPSILON : power;
    power = power > 10000 ? 10000 : power;
    return {
      In: function (t: number): number {
        return t ** power;
      },
      Out: function (t: number): number {
        return 1 - (1 - t) ** power;
      },
      InOut: function (t: number): number {
        if (t < 0.5) {
          return (t * 2) ** power / 2;
        }
        return (1 - (2 - t * 2) ** power) / 2 + 0.5;
      }
    };
  },
  // 随机
  random: function (t: number) {
    return Math.random();
  },
  // 步数
  createStep: function (numSteps: number): EasingFunction {
    return function (t: number) {
      return Math.floor(t * numSteps) / numSteps;
    };
  },
  // 增加噪音
  createNoisyEasing: function (
    easingFunction: EasingFunction,
    randomProportion = 0.1
  ): EasingFunction {
    const normalProportion = 1.0 - randomProportion;
    return function (t: number) {
      return (
        randomProportion * Math.random() + normalProportion * easingFunction(t)
      );
    };
  },
  swing: function (t: number) {
    return t + Math.sin(t * Math.PI * 2) / 2;
  },
  causeSeizures: function (t: number) {
    return t + 0.2 * Math.random() - 0.1;
  }
};

export default Easing;

export function reverseEasing(easing: EasingFunction): EasingFunction {
  switch (easing) {
    // 二次
    case Easing.Quadratic.In:
      return Easing.Quadratic.Out;
    case Easing.Quadratic.Out:
      return Easing.Quadratic.In;
    case Easing.Quadratic.InOut:
      return Easing.Quadratic.InOut;
    // 三次
    case Easing.Cubic.In:
      return Easing.Cubic.Out;
    case Easing.Cubic.Out:
      return Easing.Cubic.In;
    case Easing.Cubic.InOut:
      return Easing.Cubic.InOut;
    // 四次
    case Easing.Quartic.In:
      return Easing.Quartic.Out;
    case Easing.Quartic.Out:
      return Easing.Quartic.In;
    case Easing.Quartic.InOut:
      return Easing.Quartic.InOut;
    // 五次
    case Easing.Quintic.In:
      return Easing.Quintic.Out;
    case Easing.Quintic.Out:
      return Easing.Quintic.In;
    case Easing.Quintic.InOut:
      return Easing.Quintic.InOut;
    // 正弦曲线
    case Easing.Sinusoidal.In:
      return Easing.Sinusoidal.Out;
    case Easing.Sinusoidal.Out:
      return Easing.Sinusoidal.In;
    case Easing.Sinusoidal.InOut:
      return Easing.Sinusoidal.InOut;
    // 指数
    case Easing.Exponential.In:
      return Easing.Exponential.Out;
    case Easing.Exponential.Out:
      return Easing.Exponential.In;
    case Easing.Exponential.InOut:
      return Easing.Exponential.InOut;
    // 圆
    case Easing.Circular.In:
      return Easing.Circular.Out;
    case Easing.Circular.Out:
      return Easing.Circular.In;
    case Easing.Circular.InOut:
      return Easing.Circular.InOut;
    // 松紧带
    case Easing.Elastic.In:
      return Easing.Elastic.Out;
    case Easing.Elastic.Out:
      return Easing.Elastic.In;
    case Easing.Elastic.InOut:
      return Easing.Elastic.InOut;
    // 后退
    case Easing.Back.In:
      return Easing.Back.Out;
    case Easing.Back.Out:
      return Easing.Back.In;
    case Easing.Back.InOut:
      return Easing.Back.InOut;
    // 弹跳
    case Easing.Bounce.In:
      return Easing.Bounce.Out;
    case Easing.Bounce.Out:
      return Easing.Bounce.In;
    case Easing.Bounce.InOut:
      return Easing.Bounce.InOut;
    default:
      return easing;
  }
}
